// S-245 (Create Employee Profile) + S-246 (Mark Employee as Bench) +
// S-247 (View Bench Pool) + S-248 (Bench Aging Report).
import { apiFetch, apiRequest } from "./client";

// Employee import (one creation path: a CSV, or one row from the Add employee form)
export const getEmployeeImportTemplate = async () => {
  const { data } = await apiRequest("/employees/import/template", { method: "GET" });
  if (!data || !Array.isArray(data.columns)) throw new Error("Unexpected response: no import columns.");
  return data;
};

export const dryRunEmployeeImport = async (file) => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await apiRequest("/employees/import/dry-run", { method: "POST", body });
  return data;
};

export const applyEmployeeImport = async (token, file) => {
  const body = new FormData();
  body.append("token", token);
  body.append("file", file);
  const { data } = await apiRequest("/employees/import/apply", { method: "POST", body });
  return data;
};

export const createEmployee = async (payload) => {
  const { data } = await apiRequest("/employees", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data;
};

// 2026-08-12: unwrapped here (same fix and same reason as getAllUsers()
// in services/api/users.js) -- backend returns {employees: [...]}, this
// now always returns a plain array so callers don't each re-unwrap it.
export const getAllEmployees = async () => {
  const { data } = await apiRequest("/employees", { method: "GET" });
  return data?.employees || [];
};







export const markEmployeeOnBench = async (employeeId, reason) => {
  const { data } = await apiRequest(`/employees/${employeeId}/mark-bench`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
  return data;
};

export const removeEmployeeFromBench = async (employeeId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/remove-from-bench`, {
    method: "POST",
  });
  return data;
};










// S-DEFECT-1: Update employee certifications (Guidewire certification levels)
// Employer company (BX-US / BX-IN) and business unit; "" / null clears
export const updateEmployeeOrganization = async (employeeId, organization) => {
  const { data } = await apiRequest(`/employees/${employeeId}/organization`, {
    method: "PUT",
    body: JSON.stringify(organization),
  });
  return data;
};

// Guidewire target level (guidewire_certification_expected). The current level is derived from
// the certificates the employee holds (below), so it is not sent.
export const updateEmployeeCertifications = async (employeeId, certifications) => {
  const { data } = await apiRequest(`/employees/${employeeId}/certifications`, {
    method: "PUT",
    body: JSON.stringify(certifications),
  });
  return data;
};

// The certificate catalog, record statuses and Guidewire levels for the certificates form.
export const getCertificationOptions = async () => {
  const { data } = await apiRequest("/employees/certification-options", { method: "GET" });
  return data;
};

// Certificates the employee holds (with expiry) and the Guidewire level they give.
export const listEmployeeCertificates = async (employeeId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/certificates`, { method: "GET" });
  return data;
};

export const addEmployeeCertificate = async (employeeId, body) => {
  const { data } = await apiRequest(`/employees/${employeeId}/certificates`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
};

export const updateEmployeeCertificate = async (employeeId, recordId, changes) => {
  const { data } = await apiRequest(`/employees/${employeeId}/certificates/${recordId}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
  return data;
};

export const deleteEmployeeCertificate = async (employeeId, recordId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/certificates/${recordId}`, { method: "DELETE" });
  return data;
};

// Escalations: an open one keeps the employee away from every client; a Director resolves it.
export const getEmployeeEscalations = async (employeeId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/escalations`, { method: "GET" });
  return data;
};

export const raiseEmployeeEscalation = async (employeeId, { reason, severity }) => {
  const { data } = await apiRequest(`/employees/${employeeId}/escalations`, {
    method: "POST",
    body: JSON.stringify({ reason, severity }),
  });
  return data;
};

export const resolveEmployeeEscalation = async (escalationId, resolutionNote) => {
  const { data } = await apiRequest(`/employees/escalations/${escalationId}/resolve`, {
    method: "POST",
    body: JSON.stringify({ resolution_note: resolutionNote }),
  });
  return data;
};

// Billable (bench when off projects) or non-billable (internal staff: overhead, never bench)
export const updateEmployeeBillable = async (employeeId, billable) => {
  const { data } = await apiRequest(`/employees/${employeeId}/billable`, {
    method: "PUT",
    body: JSON.stringify({ billable }),
  });
  return data;
};

// Choices for the employee edit form (the model's own lists)
export const getEmployeeEditOptions = async () => {
  const { data } = await apiRequest("/employees/edit-options", { method: "GET" });
  return data;
};

// Name, email (only without a login), title, work location
export const updateEmployeeDetails = async (employeeId, details) => {
  const { data } = await apiRequest(`/employees/${employeeId}`, {
    method: "PATCH",
    body: JSON.stringify(details),
  });
  return data;
};

// Serving notice, exit (terminated), leave: { action, effective_date, reason }
export const changeEmployeeEmployment = async (employeeId, change) => {
  const { data } = await apiRequest(`/employees/${employeeId}/employment`, {
    method: "POST",
    body: JSON.stringify(change),
  });
  return data;
};

// Hire-Train-Deploy track: { htd_track, htd_phase?, htd_start_date? }
export const updateEmployeeHtd = async (employeeId, htd) => {
  const { data } = await apiRequest(`/employees/${employeeId}/htd`, {
    method: "PUT",
    body: JSON.stringify(htd),
  });
  return data;
};

// Everything about one employee for the profile page
export const getEmployeeProfile = async (employeeId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/profile`, { method: "GET" });
  return data;
};

// Who they report to (seated under the manager in the org chart when they have a login)
export const updateEmployeeManager = async (employeeId, managerUserId) => {
  const { data } = await apiRequest(`/employees/${employeeId}/manager`, {
    method: "PUT",
    body: JSON.stringify({ manager_user_id: managerUserId }),
  });
  return data;
};

// The signed-in person's employee id; null when their login has no employee record
export const getMyEmployeeId = async () => {
  const { data } = await apiRequest("/employees/me", { method: "GET", allowStatuses: [404] });
  return data?.employee_id || null;
};

// Profile photo or cover: kind "photo" | "cover"
export const uploadEmployeePhoto = async (employeeId, kind, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await apiRequest(`/employees/${employeeId}/photo/${kind}`, { method: "PUT", body: form });
  return data;
};

// The stored photo as a blob URL (the request needs the auth header)
export const employeePhotoUrl = async (employeeId, kind) => {
  const response = await apiFetch(`/employees/${employeeId}/photo/${kind}`);
  if (!response.ok) throw new Error(`Could not load the ${kind} (HTTP ${response.status})`);
  return URL.createObjectURL(await response.blob());
};
